import { useSubmit } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import {
  BasicButton,
  DangerButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Link,
  Modal,
  showToast,
  Text,
} from "@certn/cascadia-react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

type RemoveGroupModalProps = {
  isOpen: boolean;
  userName: string | null | undefined;
  group:
    | components["schemas"]["UserCustomerGroupsListCustomerGroups"]
    | undefined;
  onCancel: () => void;
  refreshUserGroups: () => void;
  undo: () => void;
};

export function RemoveGroupModal({
  isOpen,
  userName,
  group,
  onCancel,
  refreshUserGroups,
  undo,
}: RemoveGroupModalProps) {
  const { userId } = useParams();
  const { t } = useTranslation();

  const { submit, isSubmitting } = useSubmit(
    `/api/internal/client-portal/customer-groups/${group?.id}/remove-user/`,
    {
      throwErrors: true,
      showToastOnError: true,
      onSuccess: () => {
        showToast(
          <>
            {t("Success. {{groupName}} has been removed.", {
              groupName: group?.name,
            })}{" "}
            <Link
              onPress={() => undo()}
              linkStyle="monochrome"
              testID="undo-remove-group"
            >
              {t("Undo")}
            </Link>
          </>,
          {
            type: "success",
          }
        );
        refreshUserGroups();
        onCancel();
      },
    }
  );

  return (
    <Modal isOpen={isOpen}>
      <Dialog>
        <DialogHeader onClose={onCancel}>
          {t("Remove {{ groupName }}?", { groupName: group?.name })}
        </DialogHeader>
        <DialogContent>
          <Text style="global/body-primary">
            <Trans>
              By clicking “Remove group”, {{ userName }} will no longer have
              access to this group and any cases within this group.
            </Trans>
          </Text>
        </DialogContent>
        <DialogFooter>
          <BasicButton size="medium" onPress={onCancel} testID="cancel-button">
            <Trans>Cancel</Trans>
          </BasicButton>
          <DangerButton
            type="submit"
            size="medium"
            isLoading={isSubmitting}
            onPress={() => submit({ user_id: userId })}
            testID="remove-group-confirm-button"
          >
            <Trans>Remove group</Trans>
          </DangerButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
